import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import ProductLogo from 'account/ProductLogo'
import { LOGIN_PATH } from 'account/login/Login'
import { RoutePermissionsMapping } from 'account/permissions/mapping'

import { Permissions } from 'core/application/account/permissions'

import Icon from 'common/components/general/Icon'

import useSwitchProduct from 'common/hooks/useSwitchProduct'

import { useLoginUser } from 'common/hooks'
import userPermissionAccess from 'common/userPermissionAccess'

import IchraDecisionToolIcon from 'assets/images/IchraDecisionToolIcon'
import ReviewerAppIcon from 'assets/images/ReviewerAppIcon'

import LoginAppComponent from './LoginAppComponent'

import './LoginController.less'

export const LOGIN_CONTROLLER_PATH = '/choose-app'

interface LoginControllerState {
  permissions: Permissions[]
}

interface PeoplekeepApp {
  name: string
  logo: ReactNode
}

const LoginController: React.FC = () => {
  const [permissions, setPermissions] = useState<Permissions[]>([])
  const [user] = useLoginUser()
  const history = useHistory()

  const {
    canAccessDashboard,
    canAccessAdminDashboard,
    canAccessCsiDashboard,
    canAccessDocReviewDashboard,
    canAccessIchraDecisionTool,
  } = userPermissionAccess(user)

  const handleSwitch = useCallback(() => {
    if (canAccessAdminDashboard) history.push('/admin-dashboard')
    else history.push('/eligible-employee-dashboard')
  }, [canAccessAdminDashboard])

  const { handleSwitchProduct } = useSwitchProduct(handleSwitch)

  useEffect(() => {
    if (user) {
      setPermissions(user.permissions)
    } else {
      history.push(LOGIN_PATH)
    }
  }, [])

  const goToRoute = (permission: Permissions) => {
    const route: string | undefined = RoutePermissionsMapping.get(permission)
    if (route) {
      window.location.href = '' + route
    } else {
      history.push(LOGIN_PATH)
    }
  }

  const gchraApp: PeoplekeepApp = {
    name: 'GCHRA',
    logo: <Icon type="far fa-money-bill fa-3x" fontAwesome className="color-green-6 m-24" />,
  }

  const csiApp: PeoplekeepApp = {
    name: 'CSI',
    logo: <Icon type="far fa-cog fa-3x" fontAwesome className="color-green-6 m-24" />,
  }

  const docReviewApp: PeoplekeepApp = {
    name: 'Reviewer App',
    logo: <ReviewerAppIcon width={64} height={64} className="review_app_icon" />,
  }

  const ichraDecisionToolApp: PeoplekeepApp = {
    name: 'Ichra Decision tool',
    logo: <IchraDecisionToolIcon className="review_app_icon mb-12 mt-12" />,
  }

  return permissions.length > 0 ? (
    <div data-testid={'choose-app-page'}>
      <div className="mt-32">
        <div className="flex flex__center">
          <h2 className="color-gray-8">Choose where to login</h2>
        </div>

        <div className="flex__wrap flex__center ">
          {canAccessDashboard &&
            user.products.map((product) => (
              <LoginAppComponent
                appName=""
                logo={<ProductLogo productId={String(product.id)} hidePKLogo />}
                onClick={() => {
                  handleSwitchProduct(user.companyId, product.id)
                }}
                classname="pl-56 pr-56"
              />
            ))}
          {canAccessCsiDashboard && (
            <LoginAppComponent
              testId={'csi-app'}
              appName={csiApp.name}
              logo={csiApp.logo}
              onClick={() => {
                goToRoute(Permissions.AccessCsiDashboard)
              }}
              classname="pl-56 pr-56"
            />
          )}
          {canAccessDocReviewDashboard && (
            <LoginAppComponent
              testId={'doc-review-app'}
              appName={docReviewApp.name}
              logo={docReviewApp.logo}
              onClick={() => {
                goToRoute(Permissions.AccessDocReviewDashboard)
              }}
              classname="pl-48 pr-48"
            />
          )}
          {canAccessIchraDecisionTool && (
            <LoginAppComponent
              appName={ichraDecisionToolApp.name}
              logo={ichraDecisionToolApp.logo}
              onClick={() => {
                goToRoute(Permissions.AccessIchraDecisionTools)
              }}
            />
          )}
        </div>
      </div>
    </div>
  ) : null
}

export default LoginController
